/**
 * Platform-aware persistence adapter for Zustand's `persist` middleware.
 *
 * Native: MMKV (encrypted, synchronous, very fast).
 * Web:    localStorage (survives reload, per-origin, sync API).
 * SSR:    no-op shim (Expo web export pre-renders once; we skip disk work).
 *
 * Keep instances lazy — importing MMKV at module parse time on web will
 * throw because the JSI binding isn't present.
 */
import { Platform } from 'react-native'
import type { PersistStorage, StorageValue } from 'zustand/middleware'

type MMKVLike = {
  set: (k: string, v: string) => void
  getString: (k: string) => string | undefined
  delete: (k: string) => void
}

let mmkv: MMKVLike | null = null
function getMMKV(): MMKVLike {
  if (mmkv) return mmkv
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { MMKV } = require('react-native-mmkv') as typeof import('react-native-mmkv')
    mmkv = new MMKV({ id: 'tago-zustand' })
  } catch {
    mmkv = {
      set: () => {},
      getString: () => undefined,
      delete: () => {},
    }
  }
  return mmkv!
}

function webStorage(): Storage | null {
  if (typeof window === 'undefined' || !window.localStorage) return null
  return window.localStorage
}

/**
 * Drop-in `storage:` for `persist(…)` that round-trips JSON on the right
 * backend for the current platform. Errors are swallowed — persistence is
 * a nice-to-have here, not load-bearing.
 */
export function createPlatformStorage<T>(): PersistStorage<T> {
  return {
    getItem: (name) => {
      try {
        if (Platform.OS === 'web') {
          const raw = webStorage()?.getItem(name)
          return raw ? (JSON.parse(raw) as StorageValue<T>) : null
        }
        const raw = getMMKV().getString(name)
        return raw ? (JSON.parse(raw) as StorageValue<T>) : null
      } catch {
        return null
      }
    },
    setItem: (name, value) => {
      try {
        const serialised = JSON.stringify(value)
        if (Platform.OS === 'web') {
          webStorage()?.setItem(name, serialised)
          return
        }
        getMMKV().set(name, serialised)
      } catch {
        // disk full / quota / SSR — ignore
      }
    },
    removeItem: (name) => {
      try {
        if (Platform.OS === 'web') {
          webStorage()?.removeItem(name)
          return
        }
        getMMKV().delete(name)
      } catch {
        // ignore
      }
    },
  }
}

/**
 * The same adapter, for a store whose value contains ONE field big enough to sink the rest.
 *
 * THE FAILURE THIS REMOVES. On web `takePictureAsync` returns a `data:` URI — the JPEG itself,
 * base64'd — so a listing draft carries seventeen of them, three to five megabytes, inside the
 * single JSON string `localStorage.setItem` is handed. localStorage grants about five. When it
 * refuses, it refuses the WHOLE write: not the photos, the draft. Everything the host typed —
 * make, model, plate, price, address — stops being saved too, and the wizard goes on telling
 * them their progress is safe. It is intermittent, which is worse than broken: it fits on a
 * laptop webcam and does not fit on a good one, so it passes every test we would write.
 *
 * The draft and the captures therefore go to SEPARATE keys. The draft is small, bounded by what
 * a person can type, and always fits. The captures are attempted after it and allowed to fail
 * on their own: losing them costs a retake, while losing the draft costs the entire session.
 * The coupling was the defect — a size limit is a property of the backend, and the store should
 * not have to know it exists.
 *
 * Native is untouched. MMKV has no such ceiling, and splitting there would buy nothing.
 */
export function createSplitPlatformStorage<T>(heavyKey: keyof T & string): PersistStorage<T> {
  const base = createPlatformStorage<T>()
  if (Platform.OS !== 'web') return base

  const heavyName = (name: string) => `${name}:${heavyKey}`

  return {
    getItem: (name) => {
      try {
        const raw = webStorage()?.getItem(name)
        if (!raw) return null
        const parsed = JSON.parse(raw) as StorageValue<T>
        // Captures are optional by construction: if the second write never landed, the draft
        // still rehydrates and the checklist honestly shows the slots as empty.
        const heavyRaw = webStorage()?.getItem(heavyName(name))
        if (heavyRaw && parsed.state) {
          ;(parsed.state as Record<string, unknown>)[heavyKey] = JSON.parse(heavyRaw)
        }
        return parsed
      } catch {
        return null
      }
    },
    setItem: (name, value) => {
      const state = value.state as Record<string, unknown> | undefined
      const heavy = state?.[heavyKey]
      try {
        // The draft goes FIRST and without the captures, so nothing the photos do can reach it.
        const light = state ? { ...value, state: { ...state, [heavyKey]: undefined } } : value
        webStorage()?.setItem(name, JSON.stringify(light))
      } catch {
        // Quota with the captures already excluded means the draft itself does not fit, which
        // no split can fix. Nothing to do but leave the previous save in place.
      }
      try {
        if (heavy === undefined) {
          webStorage()?.removeItem(heavyName(name))
          return
        }
        webStorage()?.setItem(heavyName(name), JSON.stringify(heavy))
      } catch {
        // The captures did not fit. Drop the stale ones rather than restoring a draft that
        // claims photos it no longer has — a filled slot pointing at nothing is the dishonesty
        // the durable-capture work exists to prevent.
        try {
          webStorage()?.removeItem(heavyName(name))
        } catch {
          // nothing left to try
        }
      }
    },
    removeItem: (name) => {
      base.removeItem(name)
      try {
        webStorage()?.removeItem(heavyName(name))
      } catch {
        // ignore
      }
    },
  }
}
